import React, { useCallback } from 'react';

import { useDispatch } from 'react-redux';
import styled, { css } from 'styled-components';

import { Router } from '@techstyle/next-routes';
import { desktop, mobile } from '@techstyle/react-components';
import { FormattedMessage } from '@techstyle/react-intl';

import { trackExitOptionClick } from '../../actions/byo';
import { BasicButton } from '../../styles/button';
import { BasicModal } from '../../styles/modal';
import { useByoContext } from '../ByoProvider';

const dialogStyle = css`
  border-radius: 4px;
  box-shadow: 0px -2px 10px 0px ${({ theme }) => theme.colors.lightShadow4};
  padding: ${({ theme }) => theme.spacing.large}px;
  ${mobile`
    padding: 20px;
    margin: 0 12px;
  `}
`;

const ExitModal = styled(BasicModal).attrs({
  springConfig: {
    tension: 230,
  },
  dialogStyle,
  transitionFrom: {
    opacity: 0,
    transform: 'translate3d(0, -100%, 0)',
  },
  transitionEnter: {
    opacity: 1,
    transform: 'translate3d(0, 0, 0)',
  },
  transitionLeave: {
    opacity: 0,
    transform: 'translate3d(0, -100%, 0)',
  },
})``;

const Title = styled.div`
  font-weight: bold;
  text-align: center;
  font-size: 22px;
  margin-bottom: ${props => props.theme.spacing.tiny}px;
  ${mobile`
    font-size: 20px;
    margin-top: 20px;
  `}
`;

const TextContainer = styled.div`
  font-size: 14px;
  letter-spacing: 0.3px;
  text-align: center;
  margin-bottom: ${props => props.theme.spacing.small}px;
  ${desktop`
    font-size: 16px;
  `}
`;

const StayButton = styled(BasicButton).attrs({
  fullWidth: true,
  buttonStyle: css`
    margin: ${props => props.theme.spacing.small}px 0;
  `,
})``;

const LeaveButtonContainer = styled.div`
  display: block;
  margin: 0 auto;
`;

const LeaveButton = styled(BasicButton).attrs({
  buttonStyle: css`
    border: none;
    background-color: transparent;
    text-transform: none;
    font-weight: normal;
    color: ${({ theme }) => theme.colors.active};
  `,
})``;

export default function ByoExitModal() {
  const dispatch = useDispatch();
  const {
    isExitModalOpen,
    setIsExitModalOpen,
    exitByoFlow,
    isInterstitialFlow,
  } = useByoContext();

  const handleLeaveButtonClick = useCallback(() => {
    setIsExitModalOpen(false);
    if (isInterstitialFlow) {
      Router.push('/cart').then(exitByoFlow);
    } else {
      exitByoFlow();
    }
    dispatch(trackExitOptionClick('Yes'));
  }, [dispatch, exitByoFlow, isInterstitialFlow, setIsExitModalOpen]);

  const handleStayButtonClick = useCallback(() => {
    setIsExitModalOpen(false);
    dispatch(trackExitOptionClick('No'));
  }, [dispatch, setIsExitModalOpen]);

  return (
    <ExitModal
      isOpen={isExitModalOpen}
      data-autotag="are_you_sure_modal"
      onExit={() => setIsExitModalOpen(false)}
      overlayClickExits={false}
      titleId="byo_exit_modal"
    >
      <Title id="byo_exit_modal">
        <FormattedMessage
          id="site_byo.exit_modal_title"
          defaultMessage="Are you sure you want to leave?"
        />
      </Title>
      <TextContainer>
        <FormattedMessage
          id="site_byo.exit_modal_text"
          defaultMessage="Exiting will take you out of 'Build a Set'."
        />
      </TextContainer>
      <StayButton onClick={handleStayButtonClick} data-autotag="no_thanks_cta">
        <FormattedMessage
          id="site_byo.exit_modal_no_cta"
          defaultMessage="No, I want to stay"
        />
      </StayButton>
      <LeaveButtonContainer>
        <LeaveButton
          onClick={handleLeaveButtonClick}
          data-autotag="yes_sure_cta"
        >
          <FormattedMessage
            id="site_byo.exit_modal_yes_cta"
            defaultMessage="Yes, I want to leave"
          />
        </LeaveButton>
      </LeaveButtonContainer>
    </ExitModal>
  );
}
